import { type Control, Controller } from 'react-hook-form'

import { ButtonGroup } from '@/components/ui/button-group'
import { CardCheckbox } from '@/components/ui/CardCheckbox'
import { FieldError } from '@/components/ui/field-error'
import { ClaimLoadStatus, ClaimTowVehicle, ClaimTruckDriveable } from '../../../../models/claim'
import type { Step3FormValues } from './claimStep3Values'

export const DRIVEABLE_OPTIONS: { value: string; label: string }[] = [
  { value: ClaimTruckDriveable.YES, label: 'Yes' },
  { value: ClaimTruckDriveable.NO, label: 'No' },
  { value: ClaimTruckDriveable.UNSURE, label: 'Unsure' },
]

export const TOW_OPTIONS: { value: ClaimTowVehicle; label: string }[] = [
  { value: ClaimTowVehicle.NONE, label: 'None' },
  { value: ClaimTowVehicle.TRACTOR, label: 'Tractor' },
  { value: ClaimTowVehicle.TRAILER, label: 'Trailer' },
  { value: ClaimTowVehicle.OTHER_VEHICLE, label: 'Other vehicle' },
]

export const LOAD_OPTIONS: { value: string; label: string }[] = [
  { value: ClaimLoadStatus.NO_LOAD, label: 'No load' },
  { value: ClaimLoadStatus.SECURED, label: 'Yes — secured' },
  { value: ClaimLoadStatus.NOT_SECURED, label: 'Yes — not secured' },
]

/** Toggle a tow selection, keeping NONE mutually exclusive with the specific vehicles. */
function toggleTow(current: ClaimTowVehicle[], value: ClaimTowVehicle, checked: boolean): ClaimTowVehicle[] {
  if (!checked) return current.filter((x) => x !== value)
  if (value === ClaimTowVehicle.NONE) return [ClaimTowVehicle.NONE]
  return [...current.filter((x) => x !== ClaimTowVehicle.NONE), value]
}

interface ClaimVehicleStatusSectionProps {
  control: Control<Step3FormValues>
}

export function ClaimVehicleStatusSection({ control }: ClaimVehicleStatusSectionProps) {
  return (
    <>
      <div className="grid grid-cols-1 gap-5">
        <Controller
          name="truckDriveable"
          control={control}
          rules={{ validate: (v) => !!v || 'Please choose.' }}
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-neutral-700">Is the truck driveable? *</label>
              <ButtonGroup options={DRIVEABLE_OPTIONS} value={field.value} onChange={field.onChange} />
              <FieldError message={fieldState.error?.message} />
            </div>
          )}
        />
        <Controller
          name="vehiclesNeedingTow"
          control={control}
          rules={{ validate: (v) => v.length > 0 || 'Select at least one.' }}
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-neutral-700">Which vehicles needed a tow? *</label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {TOW_OPTIONS.map((o) => (
                  <CardCheckbox
                    key={o.value}
                    isChecked={field.value.includes(o.value)}
                    onChange={(checked) => field.onChange(toggleTow(field.value, o.value, checked))}
                  >
                    {o.label}
                  </CardCheckbox>
                ))}
              </div>
              <FieldError message={fieldState.error?.message} />
            </div>
          )}
        />
      </div>

      <Controller
        name="loadStatus"
        control={control}
        render={({ field }) => (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-neutral-700">Was there a load? Was it secured?</label>
            <ButtonGroup options={LOAD_OPTIONS} value={field.value} onChange={field.onChange} />
          </div>
        )}
      />
    </>
  )
}
